<template>
  <div
    class="sm:w-[50%] md:w-[40%] lg:w-[30%] bg-white dark:bg-slate-900 rounded-md"
    :class="[{ 'border-left': showAvatar }, dynamicClasses]"
  >
    <div class="w-full h-full overflow-y-scroll hide-scrollbar">
      <contact-info
        :show-close-button="showCloseButton"
        :show-avatar="showAvatar"
        :contact="contact"
        :pipelineContact="pipelineContact"
        close-icon-name="dismiss"
        @panel-close="onClose"
        @toggle-panel="onClose"
        @contact-deleted="onContactDeleted"
      />

      <div
        v-for="element in contactSidebarItems"
        :key="element.name"
        class="previous-conversation"
      >
        <div v-if="element.name === 'previous_conversation'">
          <div class="contact-sidebar-sections dark:border dark:border-slate-200">
            <h3 class="sidebar-section-title dark:border dark:border-slate-200">
              {{ $t('CONTACT_PANEL.SIDEBAR_SECTIONS.PREVIOUS_CONVERSATIONS') }}
            </h3>
            <div class="max-h-[160px] previous-conversation-lists">
              <contact-conversations
                v-if="contact.id"
                :contact-id="contact.id"
                conversation-id=""
                @conversation-selected="handleConversationSelected"
              />
            </div>
          </div>
        </div>
      </div>
      <div v-if="isAgentBotsFeatureEnabled">
        <div class="contact-sidebar-section dark:border dark:border-slate-200">
          <div>
            <div class="flex justify-between sidebar-section-title">
              <div>
                <h3 class="payment-heading">
                  {{ $t('CONTACT_PANEL.SIDEBAR_SECTIONS.PREVIOUS_PAYMENTS') }}
                </h3>
              </div>
              <div>
                <woot-button class="import-clear" @click="PaymentModal">
                  {{ $t('EXPORT_CONTACTS.NEW_PAYMENT') }}
                </woot-button>
              </div>
            </div>
            <div
              class="bg-gray-100 flex justify-between p-2 rounded-md table-header"
            >
              <div class="black-color w-1/4 flex justify-center items-center">
                Status
              </div>
              <div class="black-color w-1/4 flex justify-center items-center">
                Agent
              </div>
              <div class="black-color w-1/4 flex justify-center items-center">
                GST No.
              </div>
              <div class="black-color w-1/4 flex justify-center items-center">
                Amount
              </div>
            </div>

            <!-- ✅ Scrollable Payment List Here -->
            <div class="max-h-[160px] payment-lists space-y-2 mt-2">
              <div
                v-if="
                  ListAllPayment.length > 0 && !uiFlags.isFetchingPaymentContact
                "
              >
                <div
                  v-for="(element,index) in ListAllPayment"
                  :key="element.id"
                  class="bg-gray-100 flex justify-between p-2 rounded-md"
                  :class="{ 'bg-slate-100 dark:bg-slate-700 flex p-2': index % 2 !== 0 }"
                >
                  <div
                    class="font-semibold text-sm w-1/4 flex justify-center items-center"
                    :title="element.name"
                  >
                    {{ truncateValue(element.name) }}
                  </div>
                  <div
                    class="text-sm w-1/4 text-gray-600 flex justify-center items-center"
                    :title="element?.agent.name"
                  >
                    {{ truncateValue(element?.agent.name) || 'NA' }}
                  </div>
                  <div
                    class="flex w-1/4 justify-center text-sm items-center"
                    :title="element.gst_no"
                  >
                    {{ truncateValue(element.gst_no) || 'NA' }}
                  </div>
                  <div
                    class="flex w-1/4 justify-center text-sm items-center text-green-500"
                    :title="element.amount"
                  >
                    ₹{{ truncateValue(element.amount) }}
                  </div>
                </div>
              </div>
              <div v-else-if="uiFlags.isFetchingPaymentContact">
                <loading-state
                  v-if="uiFlags.isFetchingPaymentContact"
                  :message="emptyStateMessage"
                />
              </div>
              <div
                v-else-if="!uiFlags.isFetchingPaymentContact"
                class="text-gray-500"
              >
                No previous payments found.
              </div>
            </div>
          </div>
        </div>
      </div>

      <div>
        <new-payment
          :show="showPaymentModal"
          :contact="contact"
          @close="hidePaymentModal"
          @getNewPayment="getPaymentDetail"
        />
      </div>
      <draggable
        :list="contactSidebarItems"
        :disabled="!dragEnabled"
        class="list-group"
        ghost-class="ghost"
        @start="dragging = true"
        @end="onDragEnd"
      >
        <transition-group>
          <div
            v-for="element in contactSidebarItems"
            :key="element.name"
            class="list-group-item"
          >
            <div v-if="element.name === 'contact_attributes'">
              <accordion-item
                :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONTACT_ATTRIBUTES')"
                :is-open="isContactSidebarItemOpen('is_ct_custom_attr_open')"
                compact
                @click="
                  value => toggleSidebarUIState('is_ct_custom_attr_open', value)
                "
              >
                <custom-attributes
                  :contact-id="contact.id"
                  attribute-type="contact_attribute"
                  attribute-class="conversation--attribute"
                  :custom-attributes="contact.custom_attributes"
                  class="even"
                />
                <custom-attribute-selector
                  attribute-type="contact_attribute"
                  :contact-id="contact.id"
                />
              </accordion-item>
            </div>

            <div v-if="element.name === 'contact_attachment'">
              <accordion-item
                :title="
                  $t('CONTACT_PANEL.SIDEBAR_SECTIONS.CONTACT_ATTACHMENTS')
                "
                :is-open="isContactSidebarItemOpen('is_ct_prev_attach_open')"
                @click="
                  value => toggleSidebarUIState('is_ct_prev_attach_open', value)
                "
              >
                <attachment-view
                  v-if="contact.id"
                  :contact-id="contact.id"
                  conversation-id=""
                />
              </accordion-item>
            </div>
          </div>
        </transition-group>
      </draggable>
    </div>
  </div>
</template>

<script>
import AccordionItem from 'dashboard/components/Accordion/AccordionItem';
import ContactConversations from 'dashboard/routes/dashboard/conversation/ContactConversations';
import ContactInfo from 'dashboard/routes/dashboard/conversation/contact/ContactInfo';
import CustomAttributes from 'dashboard/routes/dashboard/conversation/customAttributes/CustomAttributes.vue';
import CustomAttributeSelector from 'dashboard/routes/dashboard/conversation/customAttributes/CustomAttributeSelector.vue';
import draggable from 'vuedraggable';
import uiSettingsMixin from 'dashboard/mixins/uiSettings';
import truncateMixin from 'dashboard/mixins/truncateMixin';
import AttachmentView from 'dashboard/routes/dashboard/conversation/contact/AttachmentView';
import NewPayment from '../../../../components/widgets/NewPayment.vue';
import { mixin as clickaway } from 'vue-clickaway';
import { mapGetters } from 'vuex';
import LoadingState from 'dashboard/components/widgets/LoadingState';
import { FEATURE_FLAGS } from '../../../../featureFlags';

export default {
  components: {
    AccordionItem,
    ContactConversations,
    ContactInfo,
    CustomAttributes,
    CustomAttributeSelector,
    draggable,
    AttachmentView,
    NewPayment,
    LoadingState,
  },
  mixins: [uiSettingsMixin, clickaway, truncateMixin],
  props: {
    contact: {
      type: Object,
      default: () => ({}),
    },
    onClose: {
      type: Function,
      default: () => {},
    },
    showAvatar: {
      type: Boolean,
      default: true,
    },
    showCloseButton: {
      type: Boolean,
      default: true,
    },
    pipelineContact: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dragEnabled: true,
      contactSidebarItems: [],
      dragging: false,
      showPaymentModal: false,
      emptyStateMessage: 'Loading Payment History',
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'payments/getUIFlags',
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
    }),
    hasContactAttributes() {
      const { custom_attributes: customAttributes } = this.contact;
      return customAttributes && Object.keys(customAttributes).length;
    },
    isSpecialRoute() {
      const { path, query } = this.$route;
      // Apply special styles only to routes matching the pattern and exclude those with `page` query parameter
      return (
        path.startsWith('/app/accounts') &&
        path.includes('/contacts/') &&
        !query.page // Exclude routes with `page` query parameter
      );
    },
    dynamicClasses() {
      return this.isSpecialRoute ? 'contact--panel' : 'special-panel-styles';
    },
    ListAllPayment() {
      console.log('Hi', this.$store.getters['payments/ListAllPayment']);
      return this.$store.getters['payments/ListAllPayment'] || [];
    },
    isAgentBotsFeatureEnabled() {
      return this.isFeatureEnabledonAccount(
        this.accountId,
        FEATURE_FLAGS.PAYMENT_DASHBOARD
      );
    },
  },
  mounted() {
    const defaultItems = this.contactSidebarItemsOrder || [];
    this.contactSidebarItems = defaultItems.filter(
      item => item.name !== 'contact_labels'
    );
    console.log('My Contact', this.contact);
    this.$store.dispatch('payments/AllPaymentContactList', {
      key: 'contact_id',
      value: this.contact.id, // assume `contact` is available
    });
  },
  methods: {
    onDragEnd() {
      this.dragging = false;
      this.updateUISettings({
        contact_sidebar_items_order: this.contactSidebarItems,
      });
    },
    handleConversationSelected(conversation) {
      this.$emit('conversation-selected', conversation);
    },
    hidePaymentModal() {
      // this.showCustomDate = false; // Toggle modal visibility
      this.showPaymentModal = false;
    },
    PaymentModal() {
      console.log('My Contact', this.contact);
      this.showPaymentModal = true;
    },
    getPaymentDetail() {
      this.$store.dispatch('payments/AllPaymentContactList', {
        key: 'contact_id',
        value: this.contact.id, // assume `contact` is available
      });
    },
    onContactDeleted() {
      this.$emit('contact-deleted');
    },
  },
};
</script>

<style lang="scss" scoped>
:deep {
  .contact--profile {
    @apply pb-3 mb-4;
  }
}
.contact--panel {
  @apply border-r border-slate-50 dark:border-slate-800/50 h-full text-sm overflow-y-auto relative;
}

.list-group {
  .list-group-item {
    @apply bg-white dark:bg-slate-900;
  }
}

.conversation--details {
  @apply py-0 px-4;
}
.special-panel-styles {
  position: absolute;
  box-shadow: 4px 4px 10px rgba(0, 0, 0, 0.5);
  height: 80%;
  width: 35%;
  top: 50%;
  z-index: 40;
  left: 50%;
  transform: translate(-50%, -50%);
  overflow-y: scroll;
  scrollbar-width: none;
}

.sidebar-section-title {
  font-size: 12px;
  font-weight: 700;
  color: #8e9499; // Tailwind gray-800
  padding: 12px 0px;
  // border: 1px solid;
  border-bottom: 1px solid #e5e7eb; // Tailwind gray-200
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.contact-sidebar-section {
  border-radius: 8px;
  margin: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.22);
  padding: 10px;
  transition: box-shadow 0.3s ease;
}
.contact-sidebar-sections{
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.22);
  padding: 16px;
  margin: 12px;
  transition: box-shadow 0.3s ease;
}
.payment-heading {
  font-size: 12px;
  font-weight: 600;
  padding-top: 12px;
  color: #8e9499;
}
.import-clear {
  font-size: 12px;
}
.w-20 {
  width: 20%;
}
.hide-scrollbar::-webkit-scrollbar {
  display: none;
}
.previous-conversation{
  border-radius: 10px;
}
.payment-lists {
  @apply overflow-hidden hover:overflow-y-auto;
}
.previous-conversation-lists{
  @apply overflow-hidden hover:overflow-y-auto
}
</style>